import { useCallback, useState } from 'react'

import { useUserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import { useMyClinicAgentConversationsQuery } from '~graphql/generated/graphql'

const HAS_ASKED_STORAGE_PREFIX = 'chckup.askChckvet.hasAskedQuestion.v1'

/**
 * Module state, not React state: it survives layout remounts on navigation
 * but resets whenever the dashboard is reopened or reloaded, which is exactly
 * how long a dismissal should last.
 */
let isIntroDismissedThisLoad = false

export type AskChckvetIntroPrompt = {
    isVisible: boolean
    /** Hide until the dashboard is next opened. */
    dismiss: () => void
}

/**
 * "Have a question?" intro for Ask Chckvet. Shows on every dashboard open
 * until the user has asked a question, and must be dismissed each time.
 *
 * "Has asked" is any Ask Chckvet conversation of theirs in this clinic. The
 * thread refetches `myClinicAgentConversations` after each send, so the
 * prompt disappears as soon as they ask. Once the server has confirmed it,
 * a per-user flag skips the lookup on later loads.
 */
export function useAskChckvetIntroPrompt(args: {
    isEnabled: boolean
    isDockOpen: boolean
}): AskChckvetIntroPrompt {
    const { userId } = useUserContext()
    const { clinic } = useClinic()
    const clinicId = clinic?.id
    const [isDismissed, setIsDismissed] = useState(isIntroDismissedThisLoad)

    const shouldCheckConversations = Boolean(
        args.isEnabled &&
        !isDismissed &&
        clinicId &&
        userId &&
        !readHasAskedQuestion(userId),
    )

    const { data } = useMyClinicAgentConversationsQuery({
        variables: { clinicId: clinicId ?? '', limit: 1 },
        skip: !shouldCheckConversations,
        fetchPolicy: 'cache-and-network',
        onCompleted: result => {
            if (result.myClinicAgentConversations.length > 0) {
                writeHasAskedQuestion(userId)
            }
        },
    })

    // Wait for the lookup so returning users never see a flash of the prompt.
    const hasNoConversations = data?.myClinicAgentConversations.length === 0

    const dismiss = useCallback(() => {
        isIntroDismissedThisLoad = true
        setIsDismissed(true)
    }, [])

    return {
        isVisible:
            shouldCheckConversations && hasNoConversations && !args.isDockOpen,
        dismiss,
    }
}

function hasAskedStorageKey(userId: string): string {
    return `${HAS_ASKED_STORAGE_PREFIX}:${userId}`
}

/** Unreadable storage reports "asked" so the prompt never nags in a loop. */
function readHasAskedQuestion(userId: string): boolean {
    if (typeof window === 'undefined') return true
    try {
        return (
            window.localStorage.getItem(hasAskedStorageKey(userId)) === 'true'
        )
    } catch {
        return true
    }
}

function writeHasAskedQuestion(userId: string): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(hasAskedStorageKey(userId), 'true')
    } catch {
        // Private mode / quota — the server lookup still hides the prompt.
    }
}
